/**
 * A copilot turn's server-sent events over `fetch` — an EventSource can't
 * POST, and the API authenticates with the session cookie. The frames are
 * parsed as they arrive and handed on in order (vNext-api `docs/copilot.md`).
 */

import { config } from '@/config/environment';
import {
  fromCopilotSelection,
  toCopilotEvent,
  type CopilotEvent,
  type CopilotSelection,
} from '@/types/copilot';

export interface SseFrame {
  id: number | null;
  event: string;
  data: string;
}

/**
 * A server-sent event stream's frames, from chunks as they arrive. A frame
 * ends at a blank line; its `data:` lines join with newlines; comments
 * (`:` — the API's opening and keep-alive) and other fields are skipped; a
 * frame with no data is dropped. CRLF ends a line as LF does, even split
 * across chunks.
 */
export class SseParser {
  private buffer = '';

  push(chunk: string): SseFrame[] {
    this.buffer = (this.buffer + chunk).replace(/\r\n/g, '\n');
    const frames: SseFrame[] = [];
    let end = this.buffer.indexOf('\n\n');
    while (end !== -1) {
      const frame = parseFrame(this.buffer.slice(0, end));
      if (frame) {
        frames.push(frame);
      }
      this.buffer = this.buffer.slice(end + 2);
      end = this.buffer.indexOf('\n\n');
    }
    return frames;
  }
}

function parseFrame(block: string): SseFrame | null {
  let id: number | null = null;
  let event = 'message';
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (line === '' || line.startsWith(':')) {
      continue;
    }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }
    if (field === 'id') {
      const n = Number(value);
      id = Number.isFinite(n) ? n : null;
    } else if (field === 'event') {
      event = value;
    } else if (field === 'data') {
      data.push(value);
    }
  }
  return data.length ? { id, event, data: data.join('\n') } : null;
}

/** The API refused the stream (a 409 says why, a 404 `not_here` that the
 * turn isn't running in the process asked). */
export class CopilotStreamError extends Error {
  readonly status: number;
  readonly detail: Record<string, unknown> | null;

  constructor(status: number, message: string, detail: Record<string, unknown> | null) {
    super(message);
    this.name = 'CopilotStreamError';
    this.status = status;
    this.detail = detail;
  }
}

async function refusal(response: Response): Promise<CopilotStreamError> {
  let detail: Record<string, unknown> | null = null;
  let message = `Error ${response.status}`;
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (body.detail && typeof body.detail === 'object') {
      detail = body.detail as Record<string, unknown>;
      message = typeof detail.message === 'string' ? detail.message : message;
    } else if (typeof body.detail === 'string') {
      message = body.detail;
    }
  } catch {
    // Not JSON: the status says enough.
  }
  if (response.status === 401) {
    message = 'Unauthorized';
  }
  return new CopilotStreamError(response.status, message, detail);
}

/** Read a stream to its end, each event handed on as it arrives. */
export async function readCopilotStream(
  url: string,
  init: RequestInit,
  onEvent: (event: CopilotEvent) => void
): Promise<void> {
  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'text/event-stream',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  if (!response.ok) {
    throw await refusal(response);
  }
  if (!response.body) {
    throw new CopilotStreamError(response.status, 'The reply came back without a stream', null);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();
  const hand = (frames: SseFrame[]) => {
    for (const frame of frames) {
      let raw: unknown;
      try {
        raw = JSON.parse(frame.data);
      } catch {
        continue;
      }
      const event = toCopilotEvent(
        frame.id ?? 0,
        frame.event,
        (raw ?? {}) as Record<string, unknown>
      );
      if (event) {
        onEvent(event);
      }
    }
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    hand(parser.push(decoder.decode(value, { stream: true })));
  }
  hand(parser.push(`${decoder.decode()}\n\n`));
}

const base = () => `${config.vnextApiUrl}/workbench`;

/** Ask the copilot: the turn starts and its events stream back. */
export function askCopilot(
  reportId: string,
  text: string,
  selection: CopilotSelection | null,
  onEvent: (event: CopilotEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  return readCopilotStream(
    `${base()}/${reportId}/copilot/messages`,
    {
      method: 'POST',
      body: JSON.stringify({ text, selection: fromCopilotSelection(selection) }),
      signal,
    },
    onEvent
  );
}

/** The rest of a running reply's events, after event `after`. */
export function followCopilot(
  reportId: string,
  messageId: string,
  after: number,
  onEvent: (event: CopilotEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  return readCopilotStream(
    `${base()}/${reportId}/copilot/messages/${messageId}/events?after=${after}`,
    { method: 'GET', signal },
    onEvent
  );
}
